import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import axios from "axios";
import { useSearchParams } from "react-router-dom";
import {
  Alert, Box, Button, Chip, CircularProgress, Dialog, DialogActions, DialogContent, DialogTitle,
  Grid, Paper, Tab, Table, TableBody, TableCell, TableHead, TableRow, Tabs, TextField, Typography,
} from "@mui/material";
import { DataGrid } from "@mui/x-data-grid";
import { ToastContainer, toast } from "react-toastify";
import "react-toastify/dist/ReactToastify.css";
import DownloadOutlinedIcon from "@mui/icons-material/DownloadOutlined";
import TodayOutlinedIcon from "@mui/icons-material/TodayOutlined";
import RefreshOutlinedIcon from "@mui/icons-material/RefreshOutlined";

import config from "../../../config";
import { getAuthToken } from "../../../utils/authStorage";
import Header from "../../../components/Header";
import {
  VERIFY_META, MEMBER_STATUS_META, chipSx, metaFor, timeOnly, apiError,
} from "./zkStatusStyles";
import { backButtonSx } from "../hpcAdminStatusStyles";

const authHeaders = () => {
  const token = getAuthToken();
  return token ? { Authorization: `Bearer ${token}` } : {};
};

// Calendar-date arithmetic in UTC so the result does not shift with the browser's time zone.
const addDays = (isoDate, days) => {
  const d = new Date(`${isoDate}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
};

const csvCell = (v) => `"${String(v ?? "").replace(/"/g, '""')}"`;

const downloadCsv = (filename, header, rows) => {
  const lines = [header, ...rows].map((cells) => cells.map(csvCell).join(","));
  // Leading BOM so Excel opens the file as UTF-8.
  const blob = new Blob(["﻿" + lines.join("\r\n")], { type: "text/csv;charset=utf-8" });
  const url = window.URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  document.body.appendChild(a);
  a.click();
  a.remove();
  window.URL.revokeObjectURL(url);
};

// Staff rows come from the device scans directly (no membership), members from hpc_attendance.
const TABS = {
  members: {
    listUrl: "/hpc/admin/attendance",
    loadError: "Could not load attendance",
    historyError: "Could not load member history",
    searchLabel: "Search name or ID",
    noRows: "No check-ins for this date",
    hint: "Click a member to see their attendance history.",
  },
  staff: {
    listUrl: "/hpc/admin/attendance/staff",
    loadError: "Could not load staff attendance",
    historyError: "Could not load staff history",
    searchLabel: "Search name or PIN",
    noRows: "No staff check-ins for this date",
    hint: "Click a staff member to see their attendance history.",
  },
};

const isStaffRow = (row) => row.membership_id === undefined;

const methodColumn = {
  field: "verify_mode", headerName: "Method", width: 130,
  renderCell: ({ value }) => {
    const m = metaFor(VERIFY_META, value);
    return (
      <Box sx={{ display: "flex", alignItems: "center", height: "100%" }}>
        <Chip size="small" label={m.label} sx={chipSx(m.color)} />
      </Box>
    );
  },
};

const memberColumns = [
  { field: "check_in", headerName: "Check-in", width: 100, valueGetter: (value, row) => timeOnly(row.check_in) },
  { field: "full_name", headerName: "Name", flex: 1, minWidth: 200 },
  { field: "membership_id", headerName: "Membership ID", width: 160 },
  methodColumn,
  { field: "punch_count", headerName: "Scans", width: 80, type: "number" },
  { field: "last_punch", headerName: "Last scan", width: 100, valueGetter: (value, row) => timeOnly(row.last_punch) },
  {
    field: "membership_status", headerName: "Membership", width: 190, sortable: false,
    renderCell: ({ row }) => {
      const m = metaFor(MEMBER_STATUS_META, row.membership_status);
      return (
        <Box sx={{ display: "flex", gap: 0.5, alignItems: "center", height: "100%" }}>
          <Chip size="small" label={m.label} sx={chipSx(m.color)} />
          {row.expired_on_day && (
            <Chip size="small" label="Expired" title={`Expiry date ${row.effective_expiry_date || row.expiry_date}`}
              sx={chipSx(MEMBER_STATUS_META.expired.color)} />
          )}
          {row.frozen_on_day && (
            <Chip size="small" label="Frozen" title="A membership hold covered this day"
              sx={chipSx(MEMBER_STATUS_META.frozen.color)} />
          )}
          {(row.blocked_on_day || row.status_on_day === "grace_period") && (
            <Chip size="small" label={metaFor(MEMBER_STATUS_META, row.status_on_day).label}
              title="Admin membership status on this day; the member was not eligible"
              sx={chipSx(MEMBER_STATUS_META.blocked.color)} />
          )}
        </Box>
      );
    },
  },
];

const staffColumns = [
  { field: "check_in", headerName: "Check-in", width: 100, valueGetter: (value, row) => timeOnly(row.check_in) },
  { field: "name", headerName: "Name", flex: 1, minWidth: 200 },
  { field: "pin", headerName: "PIN", width: 120 },
  methodColumn,
  { field: "punch_count", headerName: "Scans", width: 80, type: "number" },
  { field: "last_punch", headerName: "Last scan", width: 100, valueGetter: (value, row) => timeOnly(row.last_punch) },
];

function StatCard({ label, value, color }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, height: "100%" }}>
      <Typography variant="body2" sx={{ opacity: 0.75 }}>{label}</Typography>
      <Typography variant="h4" sx={{ fontWeight: 700, color }}>{value}</Typography>
    </Paper>
  );
}

export default function HpuAttendance() {
  const [tab, setTab] = useState("members");
  const [date, setDate] = useState(""); // filled from the server ("today" in Sri Lanka), shared by both tabs
  const [rows, setRows] = useState([]);
  const [staffRows, setStaffRows] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [debounced, setDebounced] = useState("");
  const latestLoad = useRef(0); // ignore the result of a load that was overtaken (e.g. quick tab switch)

  const [historyFor, setHistoryFor] = useState(null); // row
  const [history, setHistory] = useState(null);
  const [historyLoading, setHistoryLoading] = useState(false);
  const [range, setRange] = useState({ from: "", to: "" });

  const isStaff = tab === "staff";
  const meta = TABS[tab];

  const load = useCallback(async (forDate, which) => {
    const requestId = ++latestLoad.current;
    const setItems = which === "staff" ? setStaffRows : setRows;
    setLoading(true);
    setError("");
    try {
      const res = await axios.get(`${config.apiBaseUrl}${TABS[which].listUrl}`, {
        params: forDate ? { date: forDate } : {},
        headers: authHeaders(),
      });
      if (requestId !== latestLoad.current) return;
      setItems(res.data.items || []);
      if (!forDate) setDate(res.data.date); // server decides "today" (Asia/Colombo)
    } catch (e) {
      if (requestId !== latestLoad.current) return;
      setItems([]);
      setError(apiError(e, TABS[which].loadError));
    } finally {
      if (requestId === latestLoad.current) setLoading(false);
    }
  }, []);

  // Deep link: ?date=YYYY-MM-DD opens that day; otherwise the server decides "today" (Asia/Colombo).
  const [searchParams] = useSearchParams();
  const linkedDate = searchParams.get("date");
  useEffect(() => {
    const valid = /^\d{4}-\d{2}-\d{2}$/.test(linkedDate || "") ? linkedDate : "";
    if (valid) setDate(valid);
    load(valid, "members");
  }, [load, linkedDate]);

  useEffect(() => {
    const t = setTimeout(() => setDebounced(search.trim().toLowerCase()), 300);
    return () => clearTimeout(t);
  }, [search]);

  const changeTab = (event, value) => {
    setTab(value);
    load(date, value);
  };

  const filtered = useMemo(() => {
    if (isStaff) {
      if (!debounced) return staffRows;
      return staffRows.filter((r) => `${r.name} ${r.pin}`.toLowerCase().includes(debounced));
    }
    if (!debounced) return rows;
    return rows.filter((r) => `${r.full_name} ${r.membership_id}`.toLowerCase().includes(debounced));
  }, [isStaff, rows, staffRows, debounced]);

  const stats = useMemo(() => ({
    total: rows.length,
    face: rows.filter((r) => r.verify_mode === "face").length,
    finger: rows.filter((r) => r.verify_mode === "fingerprint").length,
    expired: rows.filter((r) => r.expired_on_day).length,
  }), [rows]);

  const staffStats = useMemo(() => ({
    total: staffRows.length,
    face: staffRows.filter((r) => r.verify_mode === "face").length,
    finger: staffRows.filter((r) => r.verify_mode === "fingerprint").length,
  }), [staffRows]);

  const exportCsv = () => {
    if (!filtered.length) {
      toast.info("Nothing to export for this date");
      return;
    }
    if (isStaff) {
      downloadCsv(
        `hpu-staff-attendance-${date}.csv`,
        ["Date", "Check-in", "Last scan", "PIN", "Name", "Method", "Scans"],
        filtered.map((r) => [
          r.attendance_date, timeOnly(r.check_in), timeOnly(r.last_punch), r.pin, r.name,
          r.verify_mode, r.punch_count,
        ]),
      );
      return;
    }
    downloadCsv(
      `hpu-attendance-${date}.csv`,
      ["Date", "Check-in", "Last scan", "Membership ID", "Name", "Method", "Scans",
        "Membership status", "Expiry date", "Effective expiry date", "Expired on this day", "Frozen on this day", "Status on this day"],
      filtered.map((r) => [
        r.attendance_date, timeOnly(r.check_in), timeOnly(r.last_punch), r.membership_id, r.full_name,
        r.verify_mode, r.punch_count, r.membership_status, r.expiry_date || "", r.effective_expiry_date || "",
        r.expired_on_day ? "YES" : "", r.frozen_on_day ? "YES" : "", r.status_on_day || "",
      ]),
    );
  };

  const loadHistory = useCallback(async (row, from, to) => {
    const staff = isStaffRow(row);
    const path = staff
      ? `/hpc/admin/attendance/staff/${encodeURIComponent(row.pin)}`
      : `/hpc/admin/attendance/member/${encodeURIComponent(row.membership_id)}`;
    setHistoryLoading(true);
    try {
      const res = await axios.get(`${config.apiBaseUrl}${path}`, { params: { from, to }, headers: authHeaders() });
      setHistory(res.data);
    } catch (e) {
      setHistory(null);
      toast.error(apiError(e, TABS[staff ? "staff" : "members"].historyError));
    } finally {
      setHistoryLoading(false);
    }
  }, []);

  const openHistory = (row) => {
    const to = date;
    const from = addDays(date, -30);
    setHistoryFor(row);
    setRange({ from, to });
    setHistory(null);
    loadHistory(row, from, to);
  };

  return (
    <Box m={{ xs: "12px", sm: "16px", md: "20px" }}>
      <ToastContainer position="top-center" theme="dark" />
      <Header title="HPU ATTENDANCE" subtitle="Gym check-ins from the attendance device (first scan of the day)" />

      <Tabs value={tab} onChange={changeTab} textColor="inherit" indicatorColor="secondary"
        sx={{ mb: 2, borderBottom: 1, borderColor: "divider" }}>
        <Tab value="members" label="Members" />
        <Tab value="staff" label="Staff" />
      </Tabs>

      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1.5, alignItems: "center", mb: 2 }}>
        <TextField
          type="date" label="Date" size="small" value={date}
          InputLabelProps={{ shrink: true }}
          onChange={(e) => { setDate(e.target.value); if (e.target.value) load(e.target.value, tab); }}
        />
        <Button variant="outlined" size="small" sx={backButtonSx} startIcon={<TodayOutlinedIcon />} onClick={() => load("", tab)}>
          Today
        </Button>
        <Button variant="outlined" size="small" sx={backButtonSx} startIcon={<RefreshOutlinedIcon />} onClick={() => load(date, tab)}>
          Refresh
        </Button>
        <TextField
          size="small" label={meta.searchLabel} value={search}
          onChange={(e) => setSearch(e.target.value)} sx={{ minWidth: 240 }}
        />
        <Box sx={{ flexGrow: 1 }} />
        <Button variant="contained" size="small" startIcon={<DownloadOutlinedIcon />} onClick={exportCsv}>
          Export CSV
        </Button>
      </Box>

      {isStaff ? (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={12} sm={4}><StatCard label="Checked in" value={staffStats.total} /></Grid>
          <Grid item xs={6} sm={4}><StatCard label="Face" value={staffStats.face} color={VERIFY_META.face.color} /></Grid>
          <Grid item xs={6} sm={4}><StatCard label="Fingerprint" value={staffStats.finger} color={VERIFY_META.fingerprint.color} /></Grid>
        </Grid>
      ) : (
        <Grid container spacing={2} sx={{ mb: 2 }}>
          <Grid item xs={6} md={3}><StatCard label="Checked in" value={stats.total} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Face" value={stats.face} color={VERIFY_META.face.color} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Fingerprint" value={stats.finger} color={VERIFY_META.fingerprint.color} /></Grid>
          <Grid item xs={6} md={3}><StatCard label="Expired membership" value={stats.expired} color={stats.expired ? MEMBER_STATUS_META.expired.color : undefined} /></Grid>
        </Grid>
      )}

      {error && <Alert severity="error" sx={{ mb: 2 }}>{error}</Alert>}

      <Box sx={{ height: "65vh" }}>
        <DataGrid
          key={tab}
          rows={filtered}
          columns={isStaff ? staffColumns : memberColumns}
          loading={loading}
          getRowId={(r) => (isStaff ? r.pin : r.id)}
          onRowClick={({ row }) => openHistory(row)}
          disableRowSelectionOnClick
          initialState={{ sorting: { sortModel: [{ field: "check_in", sort: "asc" }] } }}
          localeText={{ noRowsLabel: meta.noRows }}
          sx={{ cursor: "pointer" }}
        />
      </Box>
      <Typography variant="caption" sx={{ opacity: 0.7 }}>
        {meta.hint}
      </Typography>

      <Dialog open={!!historyFor} onClose={() => setHistoryFor(null)} maxWidth="sm" fullWidth>
        <DialogTitle>
          {historyFor && (isStaffRow(historyFor) ? historyFor.name : historyFor.full_name)}
          <Typography variant="body2" sx={{ opacity: 0.75 }}>
            {historyFor && (isStaffRow(historyFor) ? `Staff - PIN ${historyFor.pin}` : historyFor.membership_id)}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", gap: 1.5, mb: 2, flexWrap: "wrap" }}>
            <TextField type="date" size="small" label="From" value={range.from} InputLabelProps={{ shrink: true }}
              onChange={(e) => setRange((r) => ({ ...r, from: e.target.value }))} />
            <TextField type="date" size="small" label="To" value={range.to} InputLabelProps={{ shrink: true }}
              onChange={(e) => setRange((r) => ({ ...r, to: e.target.value }))} />
            <Button variant="outlined" size="small" sx={backButtonSx}
              onClick={() => historyFor && loadHistory(historyFor, range.from, range.to)}>
              Show
            </Button>
          </Box>
          {historyLoading && <CircularProgress size={24} />}
          {!historyLoading && history && (
            <>
              <Typography sx={{ mb: 1 }}>
                <b>{history.days_attended}</b> day(s) attended between {history.from} and {history.to}
              </Typography>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>Date</TableCell><TableCell>Check-in</TableCell>
                    <TableCell>Last scan</TableCell><TableCell>Method</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {history.items.map((h) => (
                    <TableRow key={h.date}>
                      <TableCell>{h.date}</TableCell>
                      <TableCell>{timeOnly(h.check_in)}</TableCell>
                      <TableCell>{timeOnly(h.last_punch)}</TableCell>
                      <TableCell>{metaFor(VERIFY_META, h.verify_mode).label}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setHistoryFor(null)}>Close</Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
}
